/**
 * Buttons: form primary/outline, footer CTAs, glass/blur stacks — reusable across flows.
 *
 * **Consumers:** `createCompactHeroCta*` → MySubscriptions card actions; `createStackedFooter*` → order result screens, address delivery modal, MySubscriptions empty CTA.
 *
 * **Theme (light/dark):** Static exports use `appTheme.colors` (snapshot at import). For surfaces that
 * must track `useThemeColors()` (appearance switches), use the `create*Glass*` factories below which
 * take `ThemeColors` from the store.
 *
 * **RTL:** Row-based controls should rely on `flexDirection: 'row'` and start/end semantics; avoid
 * hard-coded `left`/`right` insets on asymmetric layouts. See `sharedStyles.ts` barrel header.
 *
 * @see atomicRules.ts
 */
import { ViewStyle, TextStyle, StyleSheet, Platform } from 'react-native';
import { hexToRgba } from '../colorUtils';
import type { ThemeColors } from '../store';
import { appTheme } from '../appTheme';
import { androidScaleFont, androidScaleSize, isAndroidUi } from '../androidUiParity';
import { androidStripElevationSeams } from '../blurParity';

/** ~82% opaque primary tint — frosted glass + blur reads as a richer CTA than `primaryHalf` alone. */
export const FROSTED_GLASS_FILL_OPACITY = 0.82;

/** Border alpha for frosted fills — keeps edge visible on light and dark surfaces. */
export const FROSTED_GLASS_BORDER_OPACITY = 0.42;

/**
 * Tertiary frosted: light tint of brand tertiary + blur — almost transparent fill vs primary/secondary.
 * Border slightly stronger so the control stays visible on light and dark surfaces.
 */
export const FROSTED_GLASS_TERTIARY_FILL_OPACITY = 0.24;

export const FROSTED_GLASS_TERTIARY_BORDER_OPACITY = 0.38;

// --- Buttons (form / list) ---
export const primaryButtonInner: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  paddingVertical: appTheme.spacing.sm,
  paddingHorizontal: appTheme.spacing.xl,
  borderRadius: appTheme.borderRadius.md,
  backgroundColor: appTheme.colors.primary,
  minHeight: appTheme.sizes.buttonHeightLarge,
};

export const primaryButtonText: TextStyle = {
  ...appTheme.typography.body,
  fontWeight: '600',
  color: appTheme.colors.onPrimary ?? appTheme.colors.primaryText ?? appTheme.colors.white,
};

export const outlineButtonInner: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  paddingVertical: appTheme.spacing.md,
  paddingHorizontal: appTheme.spacing.xl,
  borderRadius: appTheme.borderRadius.md,
  borderWidth: 0.5,
  borderColor: appTheme.colors.border,
  backgroundColor: appTheme.colors.surface,
  minHeight: appTheme.sizes.buttonHeightLarge,
};

export const outlineButtonText: TextStyle = {
  ...appTheme.typography.body,
  fontWeight: '600',
  color: appTheme.colors.primary,
};

/**
 * `textAlign` + Android optical centering for labels in tabs, small pills, and icon-adjacent text.
 * Used by: ConsultationScreen tabs, MenuFlowFoodTypeBar, MenuFlowHeader box tabs, SubscriptionListScreen duration chips,
 * `createSegmentedGenderLabelStyle` (JourneyScreen, AllergiesDislikesScreen); NotificationsScreen filter tabs
 * and header action; LoadingState / ErrorState message copy.
 */
export const centeredLabelText: TextStyle = {
  textAlign: 'center',
  ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
};

/**
 * Full-width pill / modal footer CTA label (Save, Cancel, Accept).
 * Used by: CheckoutScreen date sheet + addons Done, TermsAndConditionsScreen accept, SubscriptionListScreen Regular/Custom tabs,
 * DailyCustomizationModal footer split CTAs.
 */
export const pillButtonLabelCentered: TextStyle = {
  ...centeredLabelText,
  width: '100%',
};

// --- Footer buttons ---
export const pageFooterBtnWrap: ViewStyle = { alignSelf: 'center', justifyContent: 'center', alignItems: 'center' };

export const pageFooterBtnInner: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: appTheme.spacing.sm,
  paddingHorizontal: appTheme.spacing.xxl * 2,
  paddingVertical: appTheme.spacing.md,
  borderRadius: appTheme.borderRadius.lg,
  borderWidth: 0.5,
  borderColor: appTheme.colors.primaryHalf,
  backgroundColor: appTheme.colors.surface,
  ...appTheme.shadows.lg,
};

export const pageFooterBtnText: TextStyle = {
  ...appTheme.typography.body,
  fontWeight: '600',
  color: appTheme.colors.primary,
};

export const coloredFooterBtnInner: ViewStyle = {
  borderWidth: 0.5,
  borderColor: appTheme.colors.primaryText,
  backgroundColor: appTheme.colors.primary,
};

export const coloredFooterBtnText: TextStyle = {
  ...appTheme.typography.body,
  fontWeight: '600',
  color: appTheme.colors.primaryText,
};

// --- Glass / blur CTA (stacked primary + outline — welcome hero, carousel footers, etc.) ---
/** Used by: `StackedGlassCtaButton`, MainScreen (via that component); `OrderCheckoutScreen` (coupon field + apply). */
export const buttonStack: ViewStyle = {
  gap: appTheme.spacing.lg,
};

export const buttonWrap: ViewStyle = {
  alignSelf: 'stretch',
  flexShrink: 0,
  ...appTheme.shadows.lg,
  shadowColor: appTheme.colors.shadowColor,
};

export const button: ViewStyle = {
  alignSelf: 'stretch',
  flexShrink: 0,
  height: 56,
  borderRadius: appTheme.borderRadius.lg,
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 0.5,
  borderColor: appTheme.colors.backgroundFade,
};

export const buttonFilled: ViewStyle = {
  backgroundColor: appTheme.colors.primaryHalf,
};

/** Secondary glass CTA — readable on gradients/photos. */
export const buttonOutlined: ViewStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.46)',
  borderWidth: 0.5,
  borderColor: appTheme.colors.primaryHalf,
};

export const buttonText: TextStyle = {
  fontSize: 18,
  fontWeight: '700',
  letterSpacing: 0.5,
  color: appTheme.colors.primaryText,
};

export const buttonTextOutline: TextStyle = {
  fontSize: 18,
  fontWeight: '700',
  letterSpacing: 0.5,
  color: appTheme.colors.primary,
};

export const buttonRow: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  gap: appTheme.spacing.sm,
};

/**
 * Solid primary CTA — full brand fill, subtle edge + shadow (credential sheets, welcome actions).
 * Used by: `LoginSheetContent` / `RegisterSheetContent` (primary CTA with `Clickable`), `DailyCustomizationModal` (apply),
 * `StackedGlassCtaButton` (`emphasis="solid"`), `ProfileScreen` v1 (save changes).
 * Pair with `Clickable` (blur optional).
 */
export function createSolidPrimaryCtaSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    alignSelf: 'stretch',
    flexShrink: 0,
    height: 46,
    borderRadius: appTheme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0.5,
    borderColor: c.primaryLight ?? c.primaryPressed ?? c.primaryLight,
    backgroundColor: c.primary,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.button,
  };
}

export function createSolidPrimaryCtaLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: c.onPrimary ?? c.primaryText ?? c.white,
  };
}

/**
 * Frosted primary glass CTA — same geometry as `button`, fill derived from `ThemeColors.primary`.
 * Used by: hero / photo overlays. For flat credential sheets prefer `createSolidPrimaryCtaSurfaceStyle`.
 */
export function createFrostedPrimaryGlassSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    alignSelf: 'stretch',
    flexShrink: 0,
    height: 56,
    borderRadius: appTheme.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0.5,
    borderColor: hexToRgba(c.primary, FROSTED_GLASS_BORDER_OPACITY),
    backgroundColor: hexToRgba(c.primary, FROSTED_GLASS_FILL_OPACITY),
  };
}

export function createFrostedPrimaryGlassLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: c.onPrimary ?? c.primaryText ?? c.white,
  };
}

/**
 * Frosted secondary glass CTA — brand `secondary` at `FROSTED_GLASS_*` opacity (richer than tertiary).
 * **Reserved for upcoming flows** (same geometry as primary frosted). Pair with `Clickable` blur.
 */
export function createFrostedSecondaryGlassSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    alignSelf: 'stretch',
    flexShrink: 0,
    height: 56,
    borderRadius: appTheme.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0.5,
    borderColor: hexToRgba(c.secondary, FROSTED_GLASS_BORDER_OPACITY),
    backgroundColor: hexToRgba(c.secondary, FROSTED_GLASS_FILL_OPACITY),
  };
}

export function createFrostedSecondaryGlassLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: c.onSecondary ?? c.secondaryText ?? c.textInverse,
  };
}

/**
 * Frosted tertiary glass CTA — almost-transparent `tertiary` tint + blur (lighter than primary/secondary frosted).
 * Used by: e.g. register submit CTA. Pair with `Clickable` blur.
 */
export function createFrostedTertiaryGlassSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    alignSelf: 'stretch',
    flexShrink: 0,
    height: 56,
    borderRadius: appTheme.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0.5,
    borderColor: hexToRgba(c.tertiary, FROSTED_GLASS_TERTIARY_BORDER_OPACITY),
    backgroundColor: hexToRgba(c.tertiary, FROSTED_GLASS_TERTIARY_FILL_OPACITY),
  };
}

export function createFrostedTertiaryGlassLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: c.tertiaryText ?? c.onTertiary ?? c.text,
  };
}

// --- Compact hero CTA (MainScreen `button` language, scaled for cards / dense rows) ---
/**
 * Used by: `MySubscriptionsScreen` (freeze / upgrade / renew).
 * Same centering contract as `button`: row, centered label; `width: '100%'` so text stays centered in flex layouts.
 */
export const COMPACT_HERO_CTA_MIN_HEIGHT = 40;

export type CompactHeroCtaVariant = 'tintAlert' | 'tintPrimary' | 'tintSuccess' | 'solidPrimary';

export function createCompactHeroCtaSurfaceStyle(c: ThemeColors, variant: CompactHeroCtaVariant): ViewStyle {
  const base: ViewStyle = {
    width: '100%',
    minHeight: COMPACT_HERO_CTA_MIN_HEIGHT,
    borderRadius: appTheme.borderRadius.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: appTheme.spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
  };
  switch (variant) {
    case 'solidPrimary':
      return {
        ...base,
        backgroundColor: c.primary,
        borderColor: c.primaryDark ?? c.primaryPressed ?? hexToRgba(c.primary, 0.55),
        ...Platform.select({
          ios: {
            shadowColor: c.shadowColor,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.12,
            shadowRadius: 4,
          },
          android: { elevation: 2 },
        }),
      };
    case 'tintAlert':
      return {
        ...base,
        backgroundColor: hexToRgba(c.alert, 0.14),
        borderColor: hexToRgba(c.alert, 0.45),
      };
    case 'tintPrimary':
      return {
        ...base,
        backgroundColor: hexToRgba(c.primary, 0.16),
        borderColor: hexToRgba(c.primary, 0.45),
      };
    case 'tintSuccess':
      return {
        ...base,
        backgroundColor: hexToRgba(c.success, 0.14),
        borderColor: hexToRgba(c.success, 0.42),
      };
    default:
      return base;
  }
}

export function createCompactHeroCtaLabelStyle(c: ThemeColors, variant: CompactHeroCtaVariant): TextStyle {
  const base: TextStyle = {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.35,
    textAlign: 'center',
  };
  switch (variant) {
    case 'solidPrimary':
      return { ...base, color: c.onPrimary ?? c.primaryText ?? c.white };
    case 'tintAlert':
      return { ...base, color: c.alert };
    case 'tintPrimary':
      return { ...base, color: c.primary };
    case 'tintSuccess':
      return { ...base, color: c.success };
    default:
      return { ...base, color: c.text };
  }
}

// --- Stacked footer CTAs (full-width, MainScreen solid primary language, order/modal footers) ---
/**
 * Used by: `OrderConfirmedScreen`, `OrderFailedScreen`, `AddressDeliveryDatesModal`, `FreezeCalendarPopup`, `DashboardReminders` (bottom sheets), `RegisterSheetContent` (terms sheet), `MySubscriptionsScreen` (empty-state CTA), `DateSelectionPopup` (calendar selection / rating / display sheet), `FoodItemDetailScreen` (footer back).
 * `flexDirection: 'row'` so optional leading icon + label stay centered as a group.
 */
export function createStackedFooterPrimarySurfaceStyle(c: ThemeColors): ViewStyle {
  const min_height = isAndroidUi() ? androidScaleSize(48) : 48;
  return {
    alignSelf: 'stretch',
    minHeight: min_height,
    borderRadius: appTheme.borderRadius.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.lg,
    // borderWidth: 0,
    // borderColor: c.primaryDark ?? c.primaryPressed ?? c.primary,
    backgroundColor: c.primary,
    shadowColor: c.shadowColor,
    ...Platform.select({
      ios: { shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.14, shadowRadius: 8 },
      android: androidStripElevationSeams(),
    }),
  };
}

export function createStackedFooterPrimaryLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: isAndroidUi() ? androidScaleFont(16) : 16,
    fontWeight: '700',
    letterSpacing: 0.45,
    textAlign: 'center',
    color: c.onPrimary ?? c.primaryText ?? c.white,
  };
}

export function createStackedFooterSecondarySurfaceStyle(c: ThemeColors): ViewStyle {
  const min_height = isAndroidUi() ? androidScaleSize(48) : 48;
  return {
    alignSelf: 'stretch',
    minHeight: min_height,
    borderRadius: appTheme.borderRadius.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.lg,
    // borderWidth: 0.5,
    // borderColor: hexToRgba(c.border, 0.65),
    backgroundColor: c.surface,
    shadowColor: c.shadowColor,
    ...Platform.select({
      ios: { shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.14, shadowRadius: 8 },
      android: androidStripElevationSeams(),
    }),
  };
}

export function createStackedFooterSecondaryLabelStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.35,
    textAlign: 'center',
    color: c.text,
  };
}

/**
 * Themed “outline” footer CTA (border + surface + primary label) — use on Address list add, etc.
 * Replaces static `pageFooterBtnInner` / `pageFooterBtnText` for light/dark.
 * Used by: `AddressListScreen` (v1 + v2), `MySubscriptions` (optional).
 */
export function createPageFooterOutlineCtaStyles(c: ThemeColors) {
  return StyleSheet.create({
    wrap: {
      alignSelf: 'center',
      justifyContent: 'center',
      alignItems: 'center',
    },
    inner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.xxl * 2,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.lg,
      borderWidth: 0.5,
      borderColor: c.primaryHalf,
      backgroundColor: c.surface,
      ...appTheme.shadows.lg,
      ...(Platform.OS === 'ios' ? { shadowColor: c.shadowColor } : {}),
    },
    text: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.primary,
    },
  });
}

/**
 * Solid primary footer CTA (same role as `coloredFooterBtnInner` + text) — theme-safe.
 * Used by: `AddressFormScreen` save, `MySubscriptions` primary footer CTA.
 */
export function createPageFooterColoredCtaStyles(c: ThemeColors) {
  return StyleSheet.create({
    wrap: {
      alignSelf: 'center',
      justifyContent: 'center',
      alignItems: 'center',
    },
    inner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.xxl * 2,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.lg,
      borderWidth: 0.5,
      borderColor: c.onPrimary,
      backgroundColor: c.primary,
      ...appTheme.shadows.lg,
      ...(Platform.OS === 'ios' ? { shadowColor: c.shadowColor } : {}),
    },
    text: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.onPrimary,
    },
  });
}
